import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "react-toastify";
import {
  User,
  Mail,
  Building2,
  Briefcase,
  Lock,
  Edit2,
  X,
  Check,
  Shield,
  ChefHat,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useMutation } from "@tanstack/react-query";
import PageHeader from "../../components/PageHeader";
import FormInput from "../../components/FormInput";
import { usuarioService } from "../../services/usuarioService";
import { filaPizzasService } from "../../services/filaPizzasService";
import { UsuarioUpdateData } from "../../lib/validations";
import { MeuPerfilProvider } from "./context/MeuPerfilContext";

const changePasswordSchema = z
  .object({
    novaSenha: z.string().min(6, "Nova senha deve ter no mínimo 6 caracteres"),
    confirmarSenha: z.string().min(1, "Confirmação de senha é obrigatória"),
  })
  .refine((data) => data.novaSenha === data.confirmarSenha, {
    message: "As senhas não coincidem",
    path: ["confirmarSenha"],
  });

type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

const getInitials = (nome: string) => {
  const words = nome.trim().split(" ");
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }
  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");
};

const MeuPerfilContent: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isTogglingFila, setIsTogglingFila] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
  });

  const updatePasswordMutation = useMutation({
    mutationFn: async (data: { novaSenha: string }) => {
      if (!user?.id) throw new Error("Usuário não encontrado");

      const updateData: UsuarioUpdateData = {
        nome: user.nome,
        email: user.email,
        senha: data.novaSenha,
        ativo: true,
        roleId: user.roleId,
        lojaId: user.lojaId,
      };

      return usuarioService.updateUsuario(user.id, updateData);
    },
    onSuccess: () => {
      toast.success("Senha alterada com sucesso!");
      reset();
      setIsChangingPassword(false);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          "Erro ao alterar senha. Tente novamente."
      );
    },
  });

  const onSubmitPassword = async (data: ChangePasswordFormData) => {
    await updatePasswordMutation.mutateAsync({
      novaSenha: data.novaSenha,
    });
  };

  if (!user) {
    return (
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="text-center">Usuário não encontrado</div>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-6 mt-6">
        <PageHeader
          title="Meu Perfil"
          description="Visualize e gerencie suas informações pessoais"
        />
      </div>

      <hr className="my-6" />

      <div className="mb-8 flex flex-col items-center justify-center rounded-xl border border-neutral-200 bg-gradient-to-br from-primary/5 to-primary/10 p-8 shadow-sm">
        <div className="mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-dark shadow-lg">
          <span className="text-3xl font-bold text-white">
            {user.nome ? getInitials(user.nome) : "U"}
          </span>
        </div>
        <h2 className="text-2xl font-bold text-neutral-900">
          {user.nome || "Usuário"}
        </h2>
        <p className="mt-1 text-sm text-neutral-500">
          {user.role?.nome || "Sem permissão"}
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm transition-shadow duration-200 hover:shadow-md">
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <User className="text-primary" size={20} />
            </div>
            <h3 className="text-lg font-semibold text-neutral-900">
              Informações Pessoais
            </h3>
          </div>
          <div className="space-y-5">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-neutral-100">
                <User className="text-neutral-600" size={18} />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium uppercase tracking-wide text-neutral-500">
                  Nome
                </label>
                <p className="mt-1 text-base font-medium text-neutral-900">
                  {user.nome || "-"}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-neutral-100">
                <Mail className="text-neutral-600" size={18} />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium uppercase tracking-wide text-neutral-500">
                  Email
                </label>
                <p className="mt-1 text-base font-medium text-neutral-900">
                  {user.email || "-"}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-neutral-100">
                <Building2 className="text-neutral-600" size={18} />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium uppercase tracking-wide text-neutral-500">
                  Loja
                </label>
                <p className="mt-1 text-base font-medium text-neutral-900">
                  {user.loja?.nome || "-"}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-neutral-100">
                <Briefcase className="text-neutral-600" size={18} />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium uppercase tracking-wide text-neutral-500">
                  Cargo
                </label>
                <div className="mt-1 flex items-center gap-2">
                  <Shield className="text-primary" size={16} />
                  <p className="text-base font-medium text-neutral-900">
                    {user.role?.nome || "-"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm transition-shadow duration-200 hover:shadow-md">
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <ChefHat className="text-primary" size={20} />
            </div>
            <h3 className="text-lg font-semibold text-neutral-900">
              Filas de Integração
            </h3>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border border-neutral-100 bg-neutral-50 px-4 py-3">
              <div className="flex items-center gap-3">
                <span className="text-xl">🧂</span>
                <div>
                  <span className="font-medium text-neutral-700">
                    Fila Salgada
                  </span>
                  <p className="text-xs text-neutral-400">
                    Receber pizzas salgadas
                  </p>
                </div>
              </div>
              <button
                type="button"
                disabled={isTogglingFila === "salgada"}
                onClick={async () => {
                  setIsTogglingFila("salgada");
                  try {
                    const result =
                      await filaPizzasService.atualizarPreferencias({
                        isFilaSalgada: !user.isFilaSalgada,
                      });
                    updateUser({
                      isFilaSalgada: result.isFilaSalgada,
                      isFilaDoce: result.isFilaDoce,
                      isFilaSalaoSalgada: result.isFilaSalaoSalgada,
                      isFilaSalaoDoce: result.isFilaSalaoDoce,
                      isFilaRodizioSalgada: result.isFilaRodizioSalgada,
                      isFilaRodizioDoce: result.isFilaRodizioDoce,
                    });
                    toast.success(
                      result.isFilaSalgada
                        ? "Fila Salgada ativada!"
                        : "Fila Salgada desativada."
                    );
                  } catch {
                    toast.error("Erro ao atualizar preferência.");
                  } finally {
                    setIsTogglingFila(null);
                  }
                }}
                className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-50 ${
                  user.isFilaSalgada ? "bg-green-500" : "bg-neutral-300"
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                    user.isFilaSalgada ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-neutral-100 bg-neutral-50 px-4 py-3">
              <div className="flex items-center gap-3">
                <span className="text-xl">🍫</span>
                <div>
                  <span className="font-medium text-neutral-700">
                    Fila Doce
                  </span>
                  <p className="text-xs text-neutral-400">
                    Receber pizzas doces
                  </p>
                </div>
              </div>
              <button
                type="button"
                disabled={isTogglingFila === "doce"}
                onClick={async () => {
                  setIsTogglingFila("doce");
                  try {
                    const result =
                      await filaPizzasService.atualizarPreferencias({
                        isFilaDoce: !user.isFilaDoce,
                      });
                    updateUser({
                      isFilaSalgada: result.isFilaSalgada,
                      isFilaDoce: result.isFilaDoce,
                      isFilaSalaoSalgada: result.isFilaSalaoSalgada,
                      isFilaSalaoDoce: result.isFilaSalaoDoce,
                      isFilaRodizioSalgada: result.isFilaRodizioSalgada,
                      isFilaRodizioDoce: result.isFilaRodizioDoce,
                    });
                    toast.success(
                      result.isFilaDoce
                        ? "Fila Doce ativada!"
                        : "Fila Doce desativada."
                    );
                  } catch {
                    toast.error("Erro ao atualizar preferência.");
                  } finally {
                    setIsTogglingFila(null);
                  }
                }}
                className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-50 ${
                  user.isFilaDoce ? "bg-green-500" : "bg-neutral-300"
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                    user.isFilaDoce ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-neutral-100 bg-neutral-50 px-4 py-3">
              <div className="flex items-center gap-3">
                <span className="text-xl">🍽️</span>
                <div>
                  <span className="font-medium text-neutral-700">
                    Salão Salgada
                  </span>
                  <p className="text-xs text-neutral-400">
                    Receber pizzas salgadas do salão (Foodfly)
                  </p>
                </div>
              </div>
              <button
                type="button"
                disabled={isTogglingFila === "salao_salgada"}
                onClick={async () => {
                  setIsTogglingFila("salao_salgada");
                  try {
                    const result =
                      await filaPizzasService.atualizarPreferencias({
                        isFilaSalaoSalgada: !user.isFilaSalaoSalgada,
                      });
                    updateUser({
                      isFilaSalgada: result.isFilaSalgada,
                      isFilaDoce: result.isFilaDoce,
                      isFilaSalaoSalgada: result.isFilaSalaoSalgada,
                      isFilaSalaoDoce: result.isFilaSalaoDoce,
                      isFilaRodizioSalgada: result.isFilaRodizioSalgada,
                      isFilaRodizioDoce: result.isFilaRodizioDoce,
                    });
                    toast.success(
                      result.isFilaSalaoSalgada
                        ? "Salão Salgada ativada!"
                        : "Salão Salgada desativada."
                    );
                  } catch {
                    toast.error("Erro ao atualizar preferência.");
                  } finally {
                    setIsTogglingFila(null);
                  }
                }}
                className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-50 ${
                  !!user.isFilaSalaoSalgada ? "bg-green-500" : "bg-neutral-300"
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                    !!user.isFilaSalaoSalgada
                      ? "translate-x-6"
                      : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-neutral-100 bg-neutral-50 px-4 py-3">
              <div className="flex items-center gap-3">
                <span className="text-xl">🍰</span>
                <div>
                  <span className="font-medium text-neutral-700">
                    Salão Doce
                  </span>
                  <p className="text-xs text-neutral-400">
                    Receber pizzas doces do salão (Foodfly)
                  </p>
                </div>
              </div>
              <button
                type="button"
                disabled={isTogglingFila === "salao_doce"}
                onClick={async () => {
                  setIsTogglingFila("salao_doce");
                  try {
                    const result =
                      await filaPizzasService.atualizarPreferencias({
                        isFilaSalaoDoce: !user.isFilaSalaoDoce,
                      });
                    updateUser({
                      isFilaSalgada: result.isFilaSalgada,
                      isFilaDoce: result.isFilaDoce,
                      isFilaSalaoSalgada: result.isFilaSalaoSalgada,
                      isFilaSalaoDoce: result.isFilaSalaoDoce,
                      isFilaRodizioSalgada: result.isFilaRodizioSalgada,
                      isFilaRodizioDoce: result.isFilaRodizioDoce,
                    });
                    toast.success(
                      result.isFilaSalaoDoce
                        ? "Salão Doce ativada!"
                        : "Salão Doce desativada."
                    );
                  } catch {
                    toast.error("Erro ao atualizar preferência.");
                  } finally {
                    setIsTogglingFila(null);
                  }
                }}
                className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-50 ${
                  !!user.isFilaSalaoDoce ? "bg-green-500" : "bg-neutral-300"
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                    !!user.isFilaSalaoDoce ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-neutral-100 bg-neutral-50 px-4 py-3">
              <div className="flex items-center gap-3">
                <span className="text-xl">🔄</span>
                <div>
                  <span className="font-medium text-neutral-700">
                    Rodízio Salgada
                  </span>
                  <p className="text-xs text-neutral-400">
                    Receber sabores da fila de rodízio salgado
                  </p>
                </div>
              </div>
              <button
                type="button"
                disabled={isTogglingFila === "rodizio_salgada"}
                onClick={async () => {
                  setIsTogglingFila("rodizio_salgada");
                  try {
                    const result =
                      await filaPizzasService.atualizarPreferencias({
                        isFilaRodizioSalgada: !user.isFilaRodizioSalgada,
                      });
                    updateUser({
                      isFilaSalgada: result.isFilaSalgada,
                      isFilaDoce: result.isFilaDoce,
                      isFilaSalaoSalgada: result.isFilaSalaoSalgada,
                      isFilaSalaoDoce: result.isFilaSalaoDoce,
                      isFilaRodizioSalgada: result.isFilaRodizioSalgada,
                      isFilaRodizioDoce: result.isFilaRodizioDoce,
                    });
                    toast.success(
                      result.isFilaRodizioSalgada
                        ? "Rodízio Salgada ativada!"
                        : "Rodízio Salgada desativada."
                    );
                  } catch {
                    toast.error("Erro ao atualizar preferência.");
                  } finally {
                    setIsTogglingFila(null);
                  }
                }}
                className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-50 ${
                  !!user.isFilaRodizioSalgada
                    ? "bg-green-500"
                    : "bg-neutral-300"
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                    !!user.isFilaRodizioSalgada
                      ? "translate-x-6"
                      : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-neutral-100 bg-neutral-50 px-4 py-3">
              <div className="flex items-center gap-3">
                <span className="text-xl">🍩</span>
                <div>
                  <span className="font-medium text-neutral-700">
                    Rodízio Doce
                  </span>
                  <p className="text-xs text-neutral-400">
                    Receber sabores da fila de rodízio doce
                  </p>
                </div>
              </div>
              <button
                type="button"
                disabled={isTogglingFila === "rodizio_doce"}
                onClick={async () => {
                  setIsTogglingFila("rodizio_doce");
                  try {
                    const result =
                      await filaPizzasService.atualizarPreferencias({
                        isFilaRodizioDoce: !user.isFilaRodizioDoce,
                      });
                    updateUser({
                      isFilaSalgada: result.isFilaSalgada,
                      isFilaDoce: result.isFilaDoce,
                      isFilaSalaoSalgada: result.isFilaSalaoSalgada,
                      isFilaSalaoDoce: result.isFilaSalaoDoce,
                      isFilaRodizioSalgada: result.isFilaRodizioSalgada,
                      isFilaRodizioDoce: result.isFilaRodizioDoce,
                    });
                    toast.success(
                      result.isFilaRodizioDoce
                        ? "Rodízio Doce ativada!"
                        : "Rodízio Doce desativada."
                    );
                  } catch {
                    toast.error("Erro ao atualizar preferência.");
                  } finally {
                    setIsTogglingFila(null);
                  }
                }}
                className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-50 ${
                  !!user.isFilaRodizioDoce ? "bg-green-500" : "bg-neutral-300"
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                    !!user.isFilaRodizioDoce
                      ? "translate-x-6"
                      : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-neutral-100 bg-neutral-50 px-4 py-3">
              <div className="flex items-center gap-3">
                <span className="text-xl">📡</span>
                <div>
                  <span className="font-medium text-neutral-700">
                    Status Integração
                  </span>
                  <p className="text-xs text-neutral-400">
                    Controlado na tela de produção
                  </p>
                </div>
              </div>
              <span
                className={`rounded-full px-3 py-1 text-xs font-bold ${
                  user.isOnlineIntegracao
                    ? "bg-green-100 text-green-700"
                    : "bg-neutral-200 text-neutral-500"
                }`}
              >
                {user.isOnlineIntegracao ? "Online" : "Offline"}
              </span>
            </div>

            {!user.isFilaSalgada &&
              !user.isFilaDoce &&
              !user.isFilaSalaoSalgada &&
              !user.isFilaSalaoDoce &&
              !user.isFilaRodizioSalgada &&
              !user.isFilaRodizioDoce && (
                <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-center text-sm text-yellow-700">
                  Ative pelo menos uma fila para receber pedidos na tela de
                  produção integrada.
                </div>
              )}
          </div>
        </div>

        <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm transition-shadow duration-200 hover:shadow-md">
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Lock className="text-primary" size={20} />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900">
                Segurança
              </h3>
            </div>
            {!isChangingPassword && (
              <button
                onClick={() => setIsChangingPassword(true)}
                className="flex items-center gap-2 rounded-lg border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 transition-all duration-200 hover:border-primary hover:bg-primary/5 hover:text-primary"
              >
                <Edit2 size={16} />
                Alterar Senha
              </button>
            )}
          </div>

          {isChangingPassword ? (
            <form
              onSubmit={handleSubmit(onSubmitPassword)}
              className="space-y-5"
            >
              <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-4">
                <p className="text-sm text-blue-800">
                  <strong>Dica:</strong> Use uma senha forte com pelo menos 6
                  caracteres, incluindo letras e números.
                </p>
              </div>

              <FormInput
                label="Nova Senha"
                type="password"
                registration={register("novaSenha")}
                error={errors.novaSenha?.message}
                placeholder="Digite sua nova senha"
              />
              <FormInput
                label="Confirmar Nova Senha"
                type="password"
                registration={register("confirmarSenha")}
                error={errors.confirmarSenha?.message}
                placeholder="Confirme sua nova senha"
              />

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsChangingPassword(false);
                    reset();
                  }}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-neutral-300 bg-white px-4 py-2.5 text-sm font-medium text-neutral-700 transition-all duration-200 hover:border-neutral-400 hover:bg-neutral-50"
                >
                  <X size={16} />
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={updatePasswordMutation.isPending}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition-all duration-200 hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {updatePasswordMutation.isPending ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Check size={16} />
                      Salvar Senha
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-6 text-center">
              <Lock className="mx-auto mb-3 text-neutral-400" size={32} />
              <p className="text-sm font-medium text-neutral-700">
                Sua senha está protegida
              </p>
              <p className="mt-1 text-xs text-neutral-500">
                Clique em "Alterar Senha" para modificar sua senha de acesso
              </p>
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

const MeuPerfil: React.FC = () => {
  return (
    <MeuPerfilProvider>
      <MeuPerfilContent />
    </MeuPerfilProvider>
  );
};

export default MeuPerfil;
